import { useNetworkElementsStore } from '../store/networkElementsStore';
import { useZoneStore } from '../store/zoneStore';
import { useHistoricalPlaybackStore } from '../store/historicalPlaybackStore';
import { networkApi } from '../infrastructure/api/NetworkApiClient';
import { applyPowerCalculationResponse, type PowerCalculationResponse } from './powerCalculationMapping';
import { fetchTrafoMeasurements } from './trafoMeasurementService';

class PowerFlowService {
  constructor() {
    this.fetchAndUpdate(); // Initial fetch on site visit
  }

  async fetchAndUpdate() {
    // Only fetch if a zone is selected
    const activeZoneId = useZoneStore.getState().activeZoneId;
    if (!activeZoneId) {
      return;
    }

    // While the map shows a historical snapshot or a Netzanschluss check frame,
    // the live poll must not overwrite the frame — the 10s tick keeps running
    // for instant live return.
    if (useHistoricalPlaybackStore.getState().mode !== 'live') {
      return;
    }

    try {
      const data = await networkApi.zoneGet<PowerCalculationResponse>('/powercalculation');

      // Discard stale responses: the user may have switched zones while the request was in flight
      // (same guard pattern as networkElementsStore.loadElements)
      if (useZoneStore.getState().activeZoneId !== activeZoneId) {
        return;
      }

      // A rewind / check frame that started while the request was in flight wins over the poll
      if (useHistoricalPlaybackStore.getState().mode !== 'live') {
        return;
      }

      // Map measurements + segments + ring flags into the store
      // (shared code path with historical playback)
      applyPowerCalculationResponse(data);

      const store = useNetworkElementsStore.getState();

      // Fetch Lemonbeat trafo measurements in parallel (same source as TrafostationDetails)
      const trafoIds = store.elements
        .filter(e => e.type === 'trafostation')
        .map(e => e.id);
      if (trafoIds.length > 0) {
        try {
          const trafoData = await fetchTrafoMeasurements(trafoIds);
          if (useZoneStore.getState().activeZoneId !== activeZoneId) {
            return;
          }
          store.updateTrafoMeasurements(trafoData);
        } catch (err) {
          console.warn('[PowerFlowService] Trafo measurement fetch failed:', err);
        }
      }
    } catch (error) {
      console.error('[PowerFlowService] Error fetching power calculations:', error);
    }
  }
}

const powerFlowService = new PowerFlowService();
export default powerFlowService;
